import React from "react";
import { ArrowRight, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  title: "Inquiry",
  description: "Begin your journey by reaching out through our secure contact form or by phone. We’ll gather initial details to ensure our practice is the right fit for your specific needs.",
  action: "Contact Us",
  link: "/ContactPage"
}, {
  title: "Evaluation",
  description: "Schedule a comprehensive intake session. This deep-dive includes genetic testing and a thorough review of your medical history to build a precise, data-driven clinical picture.",
  action: "Learn about Precision Care",
  link: "/PrecisionCarePage"
}, {
  title: "Treatment",
  description: "Receive your personalized care plan. Whether it involves medication management, therapy, or a combination, we partner with you for long-term wellness and stability.",
  action: "View Services",
  link: "/ResourcesPage"
}];
export default function NewPatientOnboarding() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="new-patient-onboarding" className="relative w-full py-24 overflow-hidden bg-background" ref={ref}>
      {}
      <style>{`
        @keyframes dashMove {
          0% { background-position: 0 0; }
          100% { background-position: 0 40px; }
        }
        .animate-dash-vertical {
          background-image: linear-gradient(to bottom, var(--color-primary) 50%, transparent 50%);
          background-size: 2px 20px;
          background-repeat: repeat-y;
          animation: dashMove 2s linear infinite;
          opacity: 0.2;
        }
      `}</style>

      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        {}
        <div className={`text-center mb-20 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-4xl font-serif text-primary tracking-tight ![padding:0px_0px_0px_5px] ![margin:5px_0px_24px_0px] md:text-5xl">
            Your Path to Precision Care
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-sans leading-relaxed">
            A seamless, three-step onboarding process designed to bridge advanced
            science with patient-centered empathy.
          </p>
        </div>

        {}
        <div className="relative">
          {steps.map((step, i) => <div key={i} data-index={i} className={`relative flex flex-col md:flex-row gap-8 md:gap-16 mb-16 last:mb-0 group transition-all duration-1000 ease-out delay-[${i * 200}ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              {}
              <div className="flex flex-col items-center md:items-end md:w-1/3 shrink-0 relative">
                {}
                <div className="relative z-10 flex items-center justify-center size-20 md:size-24 rounded-full bg-white/50 backdrop-blur-sm border border-white/60 shadow-sm">
                  <span className="font-serif text-5xl md:text-6xl text-primary/80 leading-none pt-2">
                    {i + 1}
                  </span>
                </div>

                {}
                {i !== steps.length - 1 && <div className="absolute top-24 bottom-[-4rem] md:bottom-[-4rem] left-1/2 md:left-auto md:right-[3rem] -translate-x-1/2 md:translate-x-1/2 w-[2px] h-auto animate-dash-vertical z-0 hidden md:block"></div>}
                {}
                {i !== steps.length - 1 && <div className="absolute top-20 bottom-[-4rem] left-1/2 -translate-x-1/2 w-[2px] h-auto animate-dash-vertical z-0 md:hidden"></div>}
              </div>

              {}
              <div className="flex-1 pt-2 md:pt-4 text-center md:text-left">
                <div className="relative p-8 rounded-2xl bg-white/40 backdrop-blur-md border border-white/30 shadow-sm hover:shadow-md transition-shadow duration-300">
                  <h3 className="text-2xl md:text-3xl font-serif text-primary mb-4">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                    {step.description}
                  </p>
                  
                  <div className="flex justify-center md:justify-start">
                    <Button asChild variant="ghost" className="group/btn pl-0 hover:bg-transparent hover:text-primary">
                      <Link to={step.link}>
                        <span className="text-base font-medium border-b border-primary/20 pb-0.5 group-hover/btn:border-primary transition-colors">
                          {step.action}
                        </span>
                        <ArrowRight className="ml-2 size-4 transition-transform group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>)}
        </div>

        {}
        <div className={`mt-20 text-center transition-all duration-1000 delay-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="inline-flex flex-col items-center gap-4 p-8 rounded-3xl bg-primary/5 border border-primary/10">
            <p className="text-xl font-serif text-primary">
              Ready to start your journey?
            </p>
            <Button asChild size="lg" className="rounded-full px-8 text-lg h-12 shadow-lg shadow-primary/10">
              <Link to="/ContactPage">
                Schedule Your Consultation
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}